import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MegamenuComponent } from '../megamenu/megamenu.component';
import { FooterComponent } from '../footer/footer.component';
import { CompanyConfigService, CompanyConfig } from '../../services/company-config.service';
import { Router } from '@angular/router';

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

@Component({
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrl: './faq.component.css',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MegamenuComponent,
    FooterComponent
  ]
})
export class FaqComponent {
  config: CompanyConfig;
  selectedCategory = 'All';
  
  categories = ['All', 'General', 'Services', 'Safety', 'Pricing', 'Scheduling'];

  faqs: FAQ[] = [
    // General Questions
    {
      question: 'How often should I have pest control service?',
      answer: 'We recommend quarterly treatments for most homes to maintain year-round protection. However, the frequency can vary based on your specific pest issues, property type, and environmental factors. Our technicians will assess your situation and recommend the best schedule for your needs.',
      category: 'General'
    },
    {
      question: 'What pests does your service cover?',
      answer: 'Our comprehensive pest control service covers ants, spiders, cockroaches, silverfish, earwigs, beetles, crickets, and many other common household pests. We also offer specialized services for wasps, rodents, and mosquitoes. Each treatment is customized based on the specific pests found on your property.',
      category: 'General'
    },
    {
      question: 'Do you service my area?',
      answer: 'Yes! We proudly serve communities throughout Northern Utah including Weber, Davis, and Box Elder counties. If you\'re unsure whether we service your specific location, please give us a call at our office and we\'ll be happy to confirm coverage for your area.',
      category: 'General'
    },
    {
      question: 'What should I expect during my first visit?',
      answer: 'During your initial service, our technician will conduct a thorough inspection of your property to identify pest activity, entry points, and potential problem areas. We\'ll then perform both interior and exterior treatments tailored to your specific needs. The technician will explain what was done and what to expect in the following days and weeks.',
      category: 'General'
    },

    // Services Questions
    {
      question: 'Do you treat both inside and outside my home?',
      answer: 'Yes, our comprehensive service includes both interior and exterior treatments. We treat baseboards, entry points, and problem areas inside your home, while also creating a protective barrier around your property\'s perimeter. This dual approach provides the most effective long-term pest control.',
      category: 'Services'
    },
    {
      question: 'What happens if pests come back between treatments?',
      answer: 'We stand behind our work with our Complete Pest Guarantee. If you experience pest issues with covered species between your regular scheduled visits, we\'ll return to re-treat the problem areas at no additional charge. Simply give us a call and we\'ll schedule a return visit.',
      category: 'Services'
    },
    {
      question: 'Do you offer one-time treatments?',
      answer: 'Yes, we offer one-time pest control treatments for specific problems. However, we typically recommend ongoing quarterly service for the best long-term results, as pests can return from neighboring properties or new infestations can develop over time.',
      category: 'Services'
    },
    {
      question: 'Can you help with specific pest problems like wasps or rodents?',
      answer: 'Absolutely! In addition to our general pest control service, we offer specialized treatments for wasps, hornets, rodents, and other specific pest issues. These services are tailored to the unique challenges each pest presents and may require different treatment approaches.',
      category: 'Services'
    },

    // Safety Questions
    {
      question: 'Are your treatments safe for children and pets?',
      answer: 'Yes, the safety of your family and pets is our top priority. We use EPA-approved products and follow strict application guidelines. Most treatments allow for normal activity shortly after application, though we may recommend keeping pets and children away from treated areas until they dry, typically 1-2 hours.',
      category: 'Safety'
    },
    {
      question: 'Do I need to leave my home during treatment?',
      answer: 'In most cases, you don\'t need to leave your home during treatment. Our technicians will inform you of any specific precautions for your particular service. For interior treatments, we typically recommend staying out of treated rooms until products have dried, usually 1-2 hours.',
      category: 'Safety'
    },
    {
      question: 'What should I do to prepare for service?',
      answer: 'Minimal preparation is needed. Please ensure our technician can access areas around your home\'s perimeter and any interior areas where you\'ve noticed pest activity. Remove pet food and water bowls during treatment, and inform us of any specific concerns or sensitive areas in your home.',
      category: 'Safety'
    },
    {
      question: 'Are your technicians licensed and insured?',
      answer: 'Yes, all our technicians are fully licensed by the state of Utah and our company is fully insured. Our team receives ongoing training on the latest pest control methods, safety protocols, and product applications to ensure professional, effective service.',
      category: 'Safety'
    },

    // Pricing Questions
    {
      question: 'How much does pest control service cost?',
      answer: 'Our pricing varies based on the size of your property, the type of service needed, and the frequency of treatments. We offer free inspections and quotes, so you\'ll know exactly what to expect before any work begins. Contact us for a personalized estimate for your specific needs.',
      category: 'Pricing'
    },
    {
      question: 'Do you offer any guarantees?',
      answer: 'Yes! We offer our Complete Pest Guarantee on all our services. If pests return between scheduled treatments, we\'ll come back and re-treat at no additional cost. We stand behind our work and want you to be completely satisfied with our service.',
      category: 'Pricing'
    },
    {
      question: 'Are there any contracts or long-term commitments?',
      answer: 'We offer both contract and no-contract options to fit your preferences. Many customers prefer our quarterly service plans for consistent protection and better value, but we\'re flexible and can work with your specific needs and budget.',
      category: 'Pricing'
    },

    // Scheduling Questions
    {
      question: 'How quickly can you provide service?',
      answer: 'We typically can schedule service within 1-3 business days, and we often have same-day availability for urgent pest problems. During peak seasons, scheduling may take slightly longer, but we always strive to accommodate your needs as quickly as possible.',
      category: 'Scheduling'
    },
    {
      question: 'Do I need to be home during service?',
      answer: 'While it\'s helpful if you\'re available to discuss any specific concerns, you don\'t necessarily need to be home during exterior treatments. For interior service or initial inspections, we do prefer that someone be present. We can work with your schedule to find convenient appointment times.',
      category: 'Scheduling'
    },
    {
      question: 'What are your service hours?',
      answer: `Our regular service hours are ${this.getServiceHours()}. We also offer emergency services for urgent pest problems like wasp nests or severe infestations that pose immediate safety concerns.`,
      category: 'Scheduling'
    },
    {
      question: 'How long does a typical service take?',
      answer: 'Most routine pest control services take 30-60 minutes, depending on the size of your property and the extent of treatment needed. Initial inspections and treatments may take longer as we thoroughly assess your property and establish the treatment plan.',
      category: 'Scheduling'
    }
  ];

  constructor(
    private companyConfig: CompanyConfigService,
    private router: Router
  ) {
    this.config = this.companyConfig.getConfig();
    // Initialize FAQ answers that depend on config data
    if (this.config) {
      this.initializeDynamicFaqs();
    }
  }

  private initializeDynamicFaqs() {
    if (!this.config) {
      return;
    }
    
    // Update FAQs that reference config data
    const serviceAreasIndex = this.faqs.findIndex(faq => faq.question === 'Do you service my area?');
    if (serviceAreasIndex !== -1) {
      this.faqs[serviceAreasIndex].answer = `Yes! We proudly serve ${this.getServiceAreasText()}. If you're unsure whether we service your specific location, please give us a call at our office and we'll be happy to confirm coverage for your area.`;
    }

    const serviceHoursIndex = this.faqs.findIndex(faq => faq.question === 'What are your service hours?');
    if (serviceHoursIndex !== -1) {
      this.faqs[serviceHoursIndex].answer = `Our regular service hours are ${this.getServiceHours()}. We also offer emergency services for urgent pest problems like wasp nests or severe infestations that pose immediate safety concerns.`;
    }
  }

  get filteredFaqs() {
    if (this.selectedCategory === 'All') {
      return this.faqs;
    }
    return this.faqs.filter(faq => faq.category === this.selectedCategory);
  }

  selectCategory(category: string) {
    this.selectedCategory = category;
  }

  callPhone() {
    this.companyConfig.callPhone();
  }

  navigateToContact() {
    this.router.navigate(['/contact']);
  }

  private getServiceAreasText(): string {
    if (!this.config || !this.config.serviceAreas) {
      return 'communities throughout Northern Utah including Weber, Davis, and Box Elder counties';
    }
    
    const areas = this.config.serviceAreas;
    if (areas.length <= 3) {
      return areas.join(', ');
    }
    return `${areas.slice(0, 3).join(', ')}, and ${areas.length - 3} other cities throughout Northern Utah`;
  }

  private getServiceHours(): string {
    if (!this.config || !this.config.hours) {
      return '8:00 AM - 6:00 PM Monday through Friday, 9:00 AM - 4:00 PM on Saturday';
    }
    return `${this.config.hours.weekdays} Monday through Friday, ${this.config.hours.saturday} on Saturday`;
  }
}